import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Audience, TemplateWithItems } from '@epasu/core';
import {
  PROJECT_KIND_LABELS,
  PROJECT_KINDS,
  PROJECT_RISK_LABELS,
  PROJECT_STATUS_LABELS,
  PROJECT_STATUSES,
} from '@epasu/core/constants';
import type { ProjectKind, ProjectStatus } from '@epasu/core/constants';
import {
  AlertTriangle,
  CalendarClock,
  KanbanSquare,
  LayoutTemplate,
  List,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { crm, type ProjectRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ProjectDialog } from '@/components/ProjectDialog';
import { ProjectTemplateDialog } from '@/components/ProjectTemplateDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { ProjectBoard } from '@/components/ProjectBoard';
import { ProjectProgress } from '@/components/ProjectRiskBand';
import { ProjectStatusPill } from '@/components/ProjectStatusPill';
import { useDebouncedSearch } from '@/lib/useDebouncedSearch';
import { useQuerySeededState } from '@/lib/useQuerySeededState';
import { formatDate, formatEuro } from '@/lib/utils';

/**
 * Projekte über alle Kunden hinweg.
 *
 * Projekte gab es seit Migration 0015, aber nur über den Umweg Kunden →
 * Kundenakte → Reiter „Projekte“. Wer wissen wollte, was gerade läuft, musste
 * Kunde für Kunde nachsehen. Diese Seite ist die Liste, die dabei fehlte.
 *
 * Board und Tabelle zeigen denselben Datenbestand: das Board zum Schieben,
 * die Tabelle zum Vergleichen und Sortieren nach Fälligkeit. `ProjectBoard`
 * ist unverändert dasselbe wie auf der Kundenseite — es trug laut seinem
 * eigenen Kommentar schon immer „das Board über alle Kunden“, es gab nur
 * keine Seite, die es so benutzt hätte.
 *
 * Angelegt wird hier nicht. Ein Projekt braucht einen Kunden
 * (`projects.customer_id` ist NOT NULL), und die Wahl gehört an die Stelle,
 * an der der Kunde schon feststeht — in die Kundenakte. Ein Dialog, der
 * zuerst nach dem Kunden fragt, wäre ein zweiter Weg zum selben Ziel.
 */

/** Fenster für „Diese Woche“ in Tagen. */
const WOCHE_TAGE = 7;

/**
 * Projekte, bei denen in den nächsten sieben Tagen etwas ansteht — oder
 * schon überfällig ist.
 *
 * Überfälliges zählt bewusst mit: ein Projekt, dessen Frist vorgestern
 * verstrichen ist, aus der Woche herauszuhalten wäre genau der Fall, für den
 * diese Liste da ist.
 */
function stehtAn(projekt: ProjectRow, jetzt: Date): boolean {
  const grenze = new Date(jetzt);
  grenze.setDate(grenze.getDate() + WOCHE_TAGE);

  const faellig = projekt.dueDate ? new Date(projekt.dueDate) : null;
  if (faellig && faellig <= grenze) return true;

  const termin = projekt.nextEventAt ? new Date(projekt.nextEventAt) : null;
  return Boolean(termin && termin <= grenze);
}

export function ProjectsPage() {
  const navigate = useNavigate();

  const [status, setStatus] = useState<ProjectStatus | ''>('');
  const [art, setArt] = useState<ProjectKind | ''>('');
  // Die alte Kundenseite filterte Projekte nach Zielgruppe; `projects.list`
  // nimmt den Parameter, die Seite hatte nur kein Auswahlfeld dafür.
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [audienceId, setAudienceId] = useState('');
  const [internAnlegen, setInternAnlegen] = useState(false);
  const [vorlagenOffen, setVorlagenOffen] = useState(false);
  const [mitAbgeschlossenen, setMitAbgeschlossenen] = useState(false);
  const [ansicht, setAnsicht] = useState<'board' | 'liste'>('board');
  // Wie bei Auftraegen und Aufgaben: die globale Suche schickt ihren Begriff
  // mit, damit der Treffer auf einer vorgefilterten Liste landet.
  const [suche, setSuche] = useQuerySeededState('q');

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    void crm.audiences.list().then(setAudiences);
  }, []);

  const {
    data: projekteData,
    loading,
    error,
  } = useDebouncedSearch<ProjectRow[]>(
    () =>
      crm.projects.list({
        // Ein ausdruecklich gewaehlter Endzustand braucht `includeClosed`,
        // sonst filtert der Dienst ihn gleich wieder weg — siehe
        // `listProjects`.
        includeClosed: mitAbgeschlossenen || status === 'abgeschlossen' || status === 'abgebrochen',
        ...(status ? { status } : {}),
        ...(art ? { kind: art } : {}),
        ...(audienceId ? { audienceId } : {}),
        ...(suche.trim() ? { search: suche.trim() } : {}),
      }),
    [status, art, audienceId, suche, mitAbgeschlossenen, refreshKey],
  );

  const projekte = projekteData ?? [];
  // `data === undefined` allein bliebe nach einem Fehlschlag fuer immer
  // „laedt“ — dieselbe Regel wie auf der Auftragsliste.
  const laedt = !error && (loading || projekteData === undefined);

  const jetzt = new Date();
  const diesewoche = projekte.filter((p) => stehtAn(p, jetzt));
  const schieflage = projekte.filter((p) => p.risks.length > 0);
  const gefiltert = Boolean(status || art || audienceId || suche.trim());

  return (
    <div>
      <PageHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[16rem] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Titel, Stand oder Kunde …"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                aria-label="Projekte durchsuchen"
              />
            </div>

            <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
              <FilterButton active={status === ''} onClick={() => setStatus('')}>
                Alle
              </FilterButton>
              {PROJECT_STATUSES.map((s) => (
                <FilterButton key={s} active={status === s} onClick={() => setStatus(s)}>
                  {PROJECT_STATUS_LABELS[s]}
                </FilterButton>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
              <FilterButton active={art === ''} onClick={() => setArt('')}>
                Alle
              </FilterButton>
              {PROJECT_KINDS.map((k) => (
                <FilterButton key={k} active={art === k} onClick={() => setArt(k)}>
                  {PROJECT_KIND_LABELS[k]}
                </FilterButton>
              ))}
            </div>

            <select
              className="h-10 rounded-md border border-input bg-card px-3 text-sm"
              value={audienceId}
              onChange={(e) => setAudienceId(e.target.value)}
              aria-label="Zielgruppe"
            >
              <option value="">Alle Zielgruppen</option>
              {audiences.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={mitAbgeschlossenen}
                onChange={(e) => setMitAbgeschlossenen(e.target.checked)}
              />
              Abgeschlossene
            </label>

            <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
              <FilterButton active={ansicht === 'board'} onClick={() => setAnsicht('board')}>
                <KanbanSquare className="h-4 w-4" />
                <span className="sr-only">Board</span>
              </FilterButton>
              <FilterButton active={ansicht === 'liste'} onClick={() => setAnsicht('liste')}>
                <List className="h-4 w-4" />
                <span className="sr-only">Liste</span>
              </FilterButton>
            </div>

            <Button variant="outline" onClick={() => setVorlagenOffen((o) => !o)}>
              <LayoutTemplate className="mr-2 h-4 w-4" />
              Vorlagen
            </Button>

            <Button onClick={() => setInternAnlegen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Internes Projekt
            </Button>
          </div>
        </div>
      </PageHeader>

      {internAnlegen && (
        <ProjectDialog
          onClose={() => setInternAnlegen(false)}
          onSaved={() => {
            setInternAnlegen(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {vorlagenOffen && <VorlagenKarte />}

      {error && <p className="mb-3 text-sm text-danger">{error}</p>}

      {!laedt && schieflage.length > 0 && (
        <Card className="mb-4 space-y-2 border-danger/40 bg-danger/5 p-4">
          <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-danger">
            <AlertTriangle className="h-3.5 w-3.5" />
            Braucht Aufmerksamkeit
          </h2>
          <div className="flex flex-wrap gap-2">
            {schieflage.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/projekte/${p.id}`, { state: { from: '/projekte' } })}
                className="flex items-center gap-2 rounded-md border border-danger/40 bg-background px-3 py-1.5 text-sm hover:bg-accent/40"
              >
                <span className="font-medium">{p.title}</span>
                <span className="text-xs text-danger">
                  {p.risks.map((r) => PROJECT_RISK_LABELS[r]).join(' · ')}
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {!laedt && diesewoche.length > 0 && (
        <Card className="mb-4 space-y-2 p-4">
          <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5" />
            Diese Woche
          </h2>
          <div className="flex flex-wrap gap-2">
            {diesewoche.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/projekte/${p.id}`, { state: { from: '/projekte' } })}
                className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent/40"
              >
                <span className="font-medium">{p.title}</span>
                <span className="text-xs text-muted-foreground">{p.customerName}</span>
                {p.dueDate && (
                  <span className="text-xs text-muted-foreground">
                    fällig {formatDate(p.dueDate)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Card>
      )}

      {laedt ? (
        <Card className="p-6 text-sm text-muted-foreground">Lade …</Card>
      ) : projekte.length === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground">
          {gefiltert
            ? 'Kein Projekt passt zu dieser Auswahl.'
            : 'Noch kein Projekt. Projekte entstehen in der Kundenakte — dort legt „Neues Projekt“ das erste an.'}
        </Card>
      ) : ansicht === 'board' ? (
        <ProjectBoard
          projects={projekte}
          backTo="/projekte"
          onMoved={() => setRefreshKey((k) => k + 1)}
        />
      ) : (
        <ProjectTabelle projekte={projekte} />
      )}
    </div>
  );
}

/**
 * Die hinterlegten Projektvorlagen, zum Anlegen und Ändern.
 *
 * Sitzt auf der Projektseite und nicht unter „Einstellungen“: eine Vorlage
 * entsteht, während man an Projekten arbeitet und merkt, dass sich etwas
 * wiederholt — nicht beim Einrichten des Systems.
 */
function VorlagenKarte() {
  const [vorlagen, setVorlagen] = useState<TemplateWithItems[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [bearbeiten, setBearbeiten] = useState<TemplateWithItems | 'neu' | null>(null);
  const [loeschen, setLoeschen] = useState<TemplateWithItems | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(() => {
    setLaedt(true);
    void crm.projectTemplates
      .list()
      .then(setVorlagen)
      .catch((err: unknown) =>
        setFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen'),
      )
      .finally(() => setLaedt(false));
  }, []);

  useEffect(laden, [laden]);

  return (
    <Card className="mb-4 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <LayoutTemplate className="h-4 w-4 text-muted-foreground" />
          Projektvorlagen
        </h2>
        <Button variant="outline" size="sm" onClick={() => setBearbeiten('neu')}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          Neue Vorlage
        </Button>
      </div>

      {fehler && <p className="mt-2 text-sm text-danger">{fehler}</p>}

      {laedt ? (
        <p className="mt-2 text-sm text-muted-foreground">Lade …</p>
      ) : vorlagen.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          Noch keine Vorlage. Eine lohnt sich, sobald eine Projektart zum zweiten Mal
          gleich abläuft — Website, Shop, Beratung.
        </p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {vorlagen.map((v) => {
            const phasen = v.items.filter((i) => i.kind === 'phase').length;
            const aufgaben = v.items.filter((i) => i.kind === 'task').length;
            return (
              <li
                key={v.id}
                className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{v.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {phasen} {phasen === 1 ? 'Phase' : 'Phasen'}, {aufgaben}{' '}
                    {aufgaben === 1 ? 'Aufgabe' : 'Aufgaben'}
                    {v.notes && ` · ${v.notes}`}
                  </span>
                </span>
                <Button variant="outline" size="sm" onClick={() => setBearbeiten(v)}>
                  Bearbeiten
                </Button>
                <button
                  type="button"
                  onClick={() => setLoeschen(v)}
                  className="text-muted-foreground hover:text-danger"
                  aria-label={`Vorlage ${v.name} löschen`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {bearbeiten && (
        <ProjectTemplateDialog
          template={bearbeiten === 'neu' ? undefined : bearbeiten}
          onClose={() => setBearbeiten(null)}
          onSaved={() => {
            setBearbeiten(null);
            laden();
          }}
        />
      )}

      {loeschen && (
        <ConfirmDialog
          title={`Vorlage „${loeschen.name}“ löschen?`}
          confirmLabel="Löschen"
          destructive
          onCancel={() => setLoeschen(null)}
          onConfirm={async () => {
            try {
              await crm.projectTemplates.delete(loeschen.id);
              setLoeschen(null);
              laden();
            } catch (err) {
              setFehler(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
              setLoeschen(null);
            }
          }}
        >
          {/* Projekte, die daraus entstanden sind, bleiben unberührt — beim
              Anwenden wird kopiert, nicht verknüpft. */}
          <p>Projekte, die daraus entstanden sind, bleiben unverändert.</p>
        </ConfirmDialog>
      )}
    </Card>
  );
}

function ProjectTabelle({ projekte }: { projekte: ProjectRow[] }) {
  const navigate = useNavigate();

  // Nach Faelligkeit, Projekte ohne Frist ans Ende. Ohne das stuende die
  // Liste in der Reihenfolge des Dienstes (Status, dann Position) — fuer ein
  // Board richtig, fuer eine Tabelle, in der man Fristen vergleicht, nicht.
  const sortiert = [...projekte].sort((a, b) => {
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    if (a.dueDate) return -1;
    if (b.dueDate) return 1;
    return a.title.localeCompare(b.title, 'de');
  });

  const heute = new Date().toISOString().slice(0, 10);

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Projekt</th>
              <th className="px-4 py-3 font-medium">Kunde</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Phase</th>
              <th className="w-32 px-4 py-3 font-medium">Fortschritt</th>
              <th className="px-4 py-3 font-medium">Fällig</th>
              <th className="px-4 py-3 font-medium">Nächster Termin</th>
              <th className="px-4 py-3 font-medium">Aufgaben</th>
              <th className="px-4 py-3 font-medium">Aufträge</th>
            </tr>
          </thead>
          <tbody>
            {sortiert.map((p) => {
              const ueberfaellig = Boolean(p.dueDate && p.dueDate < heute && !p.closedAt);
              return (
                <tr
                  key={p.id}
                  className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                  onClick={() => navigate(`/projekte/${p.id}`, { state: { from: '/projekte' } })}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium">{p.title}</p>
                    {p.summary && (
                      <p className="line-clamp-1 text-xs text-muted-foreground">{p.summary}</p>
                    )}
                    {p.risks.length > 0 && (
                      <p className="flex items-center gap-1 text-xs font-medium text-danger">
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        {p.risks.map((r) => PROJECT_RISK_LABELS[r]).join(' · ')}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{p.customerName}</td>
                  <td className="px-4 py-3">
                    <ProjectStatusPill status={p.status} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {p.phaseCount === 0 ? (
                      '—'
                    ) : p.currentPhaseTitle ? (
                      <>
                        <span className="block">{p.currentPhaseTitle}</span>
                        <span className="block text-xs">
                          {p.phasesDone + 1} von {p.phaseCount}
                        </span>
                      </>
                    ) : (
                      <span className="text-xs">alle {p.phaseCount} durch</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {p.progress === null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <ProjectProgress progress={p.progress} />
                    )}
                  </td>
                  <td
                    className={`px-4 py-3 ${
                      ueberfaellig ? 'font-medium text-danger' : 'text-muted-foreground'
                    }`}
                  >
                    {p.dueDate ? formatDate(p.dueDate) : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {p.nextEventAt ? formatDate(p.nextEventAt) : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {p.totalTasks === 0 ? '—' : `${p.openTasks} von ${p.totalTasks} offen`}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {p.orderCount === 0 ? '—' : formatEuro(p.orderValueCents)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center rounded-md px-3 py-1.5 text-sm transition-colors ${
        active ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
      }`}
    >
      {children}
    </button>
  );
}
